import { getCurrentTariff, calculateDistanceFare, SURCHARGES } from './ViennaTariffRules.js';

export const FareCalculator = (
  distance,
  time,
  rateDetails,
  instructionData = {},
  decimal = 2,
  tripStartTime = null,
  returnDistance = 0,
  additionalSurcharge = 0,
  promo = null
) => {
  if (distance == null || time == null || !rateDetails) {
    throw new Error('Invalid fare calculation parameters');
  }

  // ───────────────────────────────────────
  // 🕐 TARIFF SELECTION
  // ───────────────────────────────────────
  const viennaTariff = getCurrentTariff(tripStartTime);

  // ───────────────────────────────────────
  // 📏 DISTANCE & TIME
  // ───────────────────────────────────────
  const distanceKm = parseFloat(distance) || 0;
  const returnDistanceKm = parseFloat(returnDistance) || 0;
  const totalDistanceKm = distanceKm + returnDistanceKm;

  const timeSeconds = parseFloat(time) || 0;
  const timeMinutes = timeSeconds / 60;

  // ───────────────────────────────────────
  // 💵 BASE FARE
  // ───────────────────────────────────────
  const baseFare = viennaTariff.baseFare;
  const distanceFare = calculateDistanceFare(totalDistanceKm, viennaTariff);
  const timeFare = timeMinutes * viennaTariff.ratePerMinute * 0.62;

  let fareCalculation = baseFare + distanceFare + timeFare;

  // ───────────────────────────────────────
  // 📞 BOOKING FEE
  // ───────────────────────────────────────
  fareCalculation += SURCHARGES.BOOKING_FEE;

  // ───────────────────────────────────────
  // ✈️ / 🌍 SURCHARGES
  // ───────────────────────────────────────
  if (additionalSurcharge > 0) {
    fareCalculation += additionalSurcharge;
  } else if (returnDistanceKm > 0) {
    fareCalculation += SURCHARGES.OUTSIDE_VIENNA_RETURN;
  }

  // ───────────────────────────────────────
  // 📦 OPTIONS / PARCELS
  // ───────────────────────────────────────
  instructionData = instructionData || {};

  if (instructionData?.parcelTypeSelected?.amount) {
    const parcelFee = parseFloat(instructionData.parcelTypeSelected.amount);
    if (!isNaN(parcelFee) && parcelFee > 0) {
      fareCalculation += parcelFee;
    }
  }

  if (instructionData?.optionSelected?.amount) {
    const optionFee = parseFloat(instructionData.optionSelected.amount);
    if (!isNaN(optionFee) && optionFee > 0) {
      fareCalculation += optionFee;
    }
  }

  // ───────────────────────────────────────
  // ⬆️ MINIMUM FARE
  // ───────────────────────────────────────
  const minFare = parseFloat(rateDetails.min_fare) || 0;
  const subtotal = Math.max(fareCalculation, minFare);

  // ───────────────────────────────────────
  // 🏪 CONVENIENCE FEES
  // ───────────────────────────────────────
  let convenienceFee = 0;

  if (rateDetails.convenience_fees && parseFloat(rateDetails.convenience_fees) > 0) {
    if (rateDetails.convenience_fee_type === 'flat') {
      convenienceFee = parseFloat(rateDetails.convenience_fees);
    } else {
      convenienceFee = (subtotal * parseFloat(rateDetails.convenience_fees)) / 100;
    }
  }

  const totalBeforePromo = subtotal + convenienceFee;

  // ───────────────────────────────────────
  // 🎟️ PROMO LOGIC
  // ───────────────────────────────────────
  let promoDiscount = 0;
  let promoApplied = null;

  if (promo && typeof promo === 'object' && promo.expiresAt) {
    const promoExpiresAt = Number(promo.expiresAt);
    const nowTime = Date.now();

    if (promoExpiresAt > nowTime && totalBeforePromo >= 10) {
      const percent = Number(promo.discountPercent ?? promo.percent ?? 10);
      const maxDiscount = Number(promo.maxDiscount ?? 10);

      const calculatedDiscount = (totalBeforePromo * percent) / 100;
      promoDiscount = Math.min(calculatedDiscount, maxDiscount);

      promoApplied = {
        code: promo.code || null,
        percent,
        discount: Number(promoDiscount.toFixed(decimal)),
        maxDiscount,
      };
    }
  }

  const grandTotal = totalBeforePromo - promoDiscount;

  return {
    totalCost: parseFloat(subtotal.toFixed(decimal)),
    grandTotal: parseFloat(Math.max(grandTotal, 0).toFixed(decimal)),
    convenience_fees: parseFloat(convenienceFee.toFixed(decimal)),
    tariffApplied: viennaTariff.name,
    promoApplied: promoApplied ?? null,
  };
};
